import React from 'react';
import { Link } from 'react-router-dom';
import { LuArrowRight } from 'react-icons/lu';
import { useTheme } from '@/context/theme.context';
import { APP_BG } from '@/constant/theme/colors.constant';
import { IconBadge } from '../../atoms/IconBadge';
import { Text } from '../../atoms/AppText';

export type CardVariant = 1 | 2 | 3 | 4;

interface DashboardStatCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
  variant?: CardVariant;
  /** @deprecated use variant instead */
  gradientClass?: string;
  /** @deprecated use variant instead */
  ringClass?: string;
  /** data-testid for automation */
  testId?: string;
  /** Optional navigation link — card becomes clickable */
  href?: string;
}

const getAccentColors = (
  variant: CardVariant,
  primary: string,
  secondary: string,
  tertiary: string,
): [string, string] => {
  switch (variant) {
    case 1:
      return [primary, secondary];
    case 2:
      return [secondary, tertiary];
    case 3:
      return [tertiary, primary];
    case 4:
      return [primary, tertiary];
  }
};

const DashboardStatCard: React.FC<DashboardStatCardProps> = ({
  title,
  value,
  icon: Icon,
  variant = 1,
  testId,
  href,
}) => {
  const { colors } = useTheme();
  const { primary, secondary, tertiary } = colors;
  const [colorA, colorB] = getAccentColors(
    variant,
    primary,
    secondary,
    tertiary,
  );

  const cardContent = (
    <div
      data-testid={testId ?? ''}
      className={`group relative flex overflow-hidden rounded-xl ${APP_BG.base} border border-gray-100 dark:border-white/[0.06] shadow-sm ${href
        ? 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer'
        : 'hover:shadow-md cursor-default'
        } transition-all duration-300 select-none`}
    >
      {/* Large faded icon watermark — bottom-right, diagonal crop */}
      <div
        className="pointer-events-none absolute -bottom-7 -right-5 opacity-[0.07]"
        style={{ color: colorA }}
      >
        <Icon className="w-24 h-24" />
      </div>

      {/* Left accent strip with icon */}
      <div
        className="relative flex flex-col items-center justify-center w-13 sm:w-[66px] shrink-0 py-3.5 sm:py-4 overflow-hidden"
        style={{
          background: `linear-gradient(180deg, ${colorA}18 0%, ${colorB}28 100%)`,
          borderRight: `1px solid ${colorA}20`,
        }}
      >
        {/* Foreground icon badge */}
        <IconBadge
          icon={<Icon className="w-[18px] h-[18px]" />}
          size="md"
          variant="solid"
          shadow={false}
        />
      </div>

      {/* Right content */}
      <div className="flex flex-col justify-center px-3.5 py-3.5 min-w-0 flex-1">
        <Text
          as="p"
          size="md"
          weight="semibold"
          color="subtle"
          tracking="wider"
          leading="none"
          truncate
          className="mb-1"
        >
          {title}
        </Text>
        <Text
          as="p"
          size="4xl"
          weight="bold"
          color="default"
          leading="none"
          className="tracking-tight tabular-nums sm:text-[1.75rem]"
        >
          {value ?? '—'}
        </Text>
      </div>

      {/* Arrow indicator shown on hover when navigable */}
      {href && (
        <div
          className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-5 h-5 rounded-full flex items-center justify-center"
          style={{ background: `${colorA}22`, color: colorA }}
        >
          <LuArrowRight size={11} strokeWidth={2.5} />
        </div>
      )}
    </div>
  );

  if (href) {
    return (
      <Link to={href} className="block no-underline">
        {cardContent}
      </Link>
    );
  }
  return cardContent;
};

export default DashboardStatCard;
